import { useEffect, useState } from 'react';
import { api } from './api';
import { getMovementHistory } from './movement';
import { zonedParts } from './tz';
import { nightWindow, summariseNight, flagNights, SILENT_COVERAGE } from './nightReport';

// One colour language for movement everywhere. Red stays reserved for real alerts.
export const MV = {
  moving: { dot: '#2E7D32', bg: '#E8F5E9', text: '#1B5E20', bar: '#3E8E47' },
  rest: { dot: '#728976', bg: '#EEF3EF', text: '#2F3A33', bar: '#CBD8CE' },
  quiet: { dot: '#D97706', bg: '#FEF3C7', text: '#92400E', bar: '#F2C46B' },
  sensor: { dot: '#6B7280', bg: '#F3F4F6', text: '#374151', bar: '#E5E7EB' },
};
export const HATCH = 'repeating-linear-gradient(45deg, #E5E7EB 0 3px, #C4C8CE 3px 5px)';

export const viewTone = (state) => ({
  moving: 'moving', occupied: 'moving', still: 'rest', vacant: 'rest', inactive: 'quiet',
}[state] || (state === 'no_motion_data' || state === 'never_reported' ? 'nodata' : 'sensor'));

const toMin = (hhmm) => {
  const [h, m] = String(hhmm || '00:00').slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};
export function isNightAt(ms, policy) {
  const m = zonedParts(ms, policy.timezone || 'UTC').minutes;
  const s = toMin(policy.night_hours_start);
  const e = toMin(policy.night_hours_end);
  return s > e ? m >= s || m < e : m >= s && m < e;
}

// Fixed-size segments over [fromMs, toMs): moving / rest / gap (sensor not reporting).
export function segmentsBetween(buckets, ids, fromMs, toMs, segMin = 5) {
  const step = segMin * 60000;
  const keep = ids ? new Set(ids) : null;
  const start = Math.floor(fromMs / step) * step;
  const map = new Map();
  for (const b of buckets) {
    if (keep && !keep.has(b.group_key)) continue;
    const ts = Math.floor(new Date(b.bucket_start).getTime() / step) * step;
    if (ts < start || ts >= toMs) continue;
    const cur = map.get(ts) || { samples: 0, motion: 0 };
    cur.samples += b.samples || 0;
    cur.motion += b.motion_samples || 0;
    map.set(ts, cur);
  }
  const out = [];
  for (let ts = start; ts < toMs; ts += step) {
    const v = map.get(ts);
    out.push({ ts, segMin, kind: !v || !v.samples ? 'gap' : v.motion > 0 ? 'moving' : 'rest' });
  }
  return out;
}

// 15-min segments over the last `hours`, ending with the current segment.
export function toSegments(buckets, ids, nowMs = Date.now(), hours = 24, segMin = 15) {
  const step = segMin * 60000;
  const end = Math.floor(nowMs / step) * step + step;
  return segmentsBetween(buckets, ids, end - hours * 3600000, end, segMin);
}

// n nights starting `startOffset` nights back, newest first, each with a family-friendly verdict.
export function lastNights(buckets, ids, policy, nowMs = Date.now(), n = 7, startOffset = 0) {
  const silence = policy.device_silence_threshold_min || 30;
  const nights = Array.from({ length: n }, (_, i) => {
    const w = nightWindow(policy, nowMs, startOffset + i);
    return { offset: startOffset + i, window: w, summary: summariseNight(buckets, ids, w, silence) };
  });
  return flagNights(nights).map((x) => {
    const s = x.summary;
    const kind = s.coverage === 0 ? 'nodata' : s.coverage < SILENT_COVERAGE ? 'silent'
      : x.flag === 'restless' ? 'restless' : x.flag === 'still' ? 'still' : 'calm';
    return { ...x, kind };
  });
}

// Night window/timezone. /night-summary exposes it to every role (alert-policy is admin-only).
const policyCache = new Map();
export function useNightPolicy(orgId) {
  const [policy, setPolicy] = useState(() => policyCache.get(orgId) || null);
  useEffect(() => {
    if (!orgId || policyCache.has(orgId)) { setPolicy(policyCache.get(orgId) || null); return undefined; }
    let alive = true;
    api.get(`/organizations/${orgId}/night-summary`)
      .then((r) => r.data?.window || {})
      .catch(() => ({}))
      .then((w) => {
        const p = {
          timezone: w.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          night_hours_start: w.night_hours_start || '22:00',
          night_hours_end: w.night_hours_end || '06:00',
          device_silence_threshold_min: 30,
        };
        policyCache.set(orgId, p);
        if (alive) setPolicy(p);
      });
    return () => { alive = false; };
  }, [orgId]);
  return policy;
}

// 5-min history refreshed every 5 minutes. `params` scopes it (patient_id / room_id / hours).
export function useMovementBuckets(params, enabled = true) {
  const [buckets, setBuckets] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => getMovementHistory({ resolution: '5m', ...params })
      .then((b) => alive && setBuckets(b))
      .catch(() => alive && setBuckets((prev) => prev || []));
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 300000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  return buckets;
}
